/**
 * Iter 67ba — Vendor invoice branding settings.
 *
 * Lives inside the vendor's settings panel. Lets vendors override the
 * invoice footer HTML, payment instructions, and per-vendor number prefix.
 * Falls back to the vendor's agreement branding when fields are empty.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, FileText, Save } from "lucide-react";
import { toast } from "sonner";

export default function VendorInvoiceBrandingPanel() {
  const [form, setForm] = useState({
    invoice_footer_html: "",
    invoice_payment_instructions: "",
    invoice_number_prefix: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => {
        const u = r.data || {};
        setForm({
          invoice_footer_html: u.invoice_footer_html || "",
          invoice_payment_instructions: u.invoice_payment_instructions || "",
          invoice_number_prefix: u.invoice_number_prefix || "",
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put("/vendor/invoice-branding", form);
      toast.success("Invoice branding saved.");
    } catch (err) {
      toast.error(formatApiError(err) || "Could not save branding.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-neutral-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  return (
    <div className="bg-white rounded-2xl border border-border shadow-sm p-6 space-y-4" data-testid="vendor-invoice-branding-panel">
      <div className="flex items-center gap-2.5">
        <div className="h-9 w-9 rounded-xl bg-indigo-50 ring-1 ring-indigo-100 flex items-center justify-center">
          <FileText className="h-4.5 w-4.5 text-indigo-700" />
        </div>
        <div>
          <div className="font-semibold text-neutral-900">Invoice branding</div>
          <div className="text-xs text-neutral-500">Override the defaults used on your rental invoices.</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="branding-prefix">Invoice number prefix</Label>
          <Input
            id="branding-prefix"
            value={form.invoice_number_prefix}
            onChange={(e) => setForm((f) => ({ ...f, invoice_number_prefix: e.target.value }))}
            placeholder="INV-"
            className="mt-1 rounded-xl"
            data-testid="branding-prefix"
          />
          <div className="text-[11px] text-neutral-500 mt-1">Defaults to <code>INV-</code>. Numbers reset every January.</div>
        </div>
        <div>
          <Label htmlFor="branding-payment">Payment instructions</Label>
          <Textarea
            id="branding-payment"
            value={form.invoice_payment_instructions}
            onChange={(e) => setForm((f) => ({ ...f, invoice_payment_instructions: e.target.value }))}
            placeholder={"IBAN: RO00 BTRL ...\nPayable to: My Rentals SRL"}
            className="mt-1 rounded-xl"
            rows={3}
            data-testid="branding-payment-instructions"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="branding-footer">Footer HTML</Label>
        <Textarea
          id="branding-footer"
          value={form.invoice_footer_html}
          onChange={(e) => setForm((f) => ({ ...f, invoice_footer_html: e.target.value }))}
          placeholder="<em>Thank you for renting with us.</em> All sales include statutory VAT where applicable."
          className="mt-1 rounded-xl font-mono text-xs"
          rows={4}
          data-testid="branding-footer-html"
        />
        <div className="text-[11px] text-neutral-500 mt-1">
          Sanitised before render. Allowed tags: <code>em</code>, <code>strong</code>, <code>br</code>, <code>p</code>, <code>a</code>.
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="branding-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save branding
        </Button>
      </div>
    </div>
  );
}
